import { computed, makeObservable, observable, runInAction } from 'mobx'

import {
  DefaultError,
  MutateOptions,
  MutationObserver,
  MutationObserverOptions,
  MutationObserverResult,
  QueryClient,
} from '@tanstack/react-query'

import { queryClient as globalQueryClient } from '../query-client'

interface ObservableMutationConfig {
  queryClient?: QueryClient
}

/**
 * MobX-compatible wrapper for react-query mutations. Use it inside MobX stores
 * to perform write operations with automatic pending/error state tracking.
 *
 * @example
 * // Basic mutation with cache invalidation
 * private _saveUserMutation = new ObservableMutation({
 *   mutationFn: (payload: SaveUserPayload) => saveUser(payload),
 *   onSuccess: () => this._usersQuery.invalidate(),
 * })
 *
 * async saveUser(payload: SaveUserPayload) {
 *   return this._saveUserMutation.mutate(payload, {
 *     onSuccess: () => notify('Saved!'),
 *     onError: (error) => reportError(error),
 *   })
 * }
 *
 * @example
 * // Reading mutation state
 * get isSaving() { return this._saveUserMutation.isPending }
 * get saveError() { return this._saveUserMutation.error }
 */
export class ObservableMutation<
  TData = unknown,
  TVariables = void,
  TError = DefaultError,
  TContext = unknown,
> {
  private _result: MutationObserverResult<TData, TError, TVariables, TContext>
  private _observer: MutationObserver<TData, TError, TVariables, TContext>
  private _unsubscribe: () => void

  constructor(
    options: MutationObserverOptions<TData, TError, TVariables, TContext>,
    config?: ObservableMutationConfig,
  ) {
    this._observer = new MutationObserver(config?.queryClient ?? globalQueryClient, options)
    this._result = this._observer.getCurrentResult()

    makeObservable<ObservableMutation<TData, TVariables, TError, TContext>, '_result'>(this, {
      _result: observable.ref,
      data: computed,
      isPending: computed,
      error: computed,
    })

    this._unsubscribe = this._observer.subscribe(result =>
      runInAction(() => (this._result = result)),
    )
  }

  get data() {
    return this._result.data
  }

  get isPending() {
    return this._result.isPending
  }

  get error() {
    return this._result.error
  }

  mutate(
    variables: TVariables,
    options?: MutateOptions<TData, TError, TVariables, TContext>,
  ): Promise<TData> {
    return this._observer.mutate(variables, options)
  }

  dispose() {
    this._unsubscribe()
  }
}
